<template>
  <div ref="dropRef" class="w-100 position-relative">
    <slot :result="result" v-bind="{ open, reset }"></slot>
  </div>
</template>
<script>
import { useFileDialog } from "@/composables/useFileDialog";
import { isSafeArray, safeArray, size } from "@/helpers/functions/ArrayHelpers";
import { useEventListener } from "@vueuse/core";
import { computed, defineComponent, readonly, ref, shallowRef } from "vue";

export default defineComponent({
  props: {
    multiple: {
      type: Boolean,
      default: true,
    },
    accept: {
      type: String,
      default: "*",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["enter", "over", "leave", "drop"],
  setup(props, { emit }) {
    const dropRef = ref();
    const files = shallowRef(null);
    const isOverDropzone = shallowRef(false);

    const { open, reset, onChange, onReset } = useFileDialog({
      initialFiles: files,
      disabled: computed(() => props.disabled),
      multiple: computed(() => props.multiple),
      accept: computed(() => props.accept),
    });

    const allowedTypes = computed(() => String(props.accept).split(","));

    const fileSizeTotal = computed(() =>
      safeArray(files.value).reduce((counter, file) => {
        counter += isNaN(file.size) ? 0 : file.size;
        return counter;
      }, 0)
    );

    const result = computed(() => {
      const isArray = isSafeArray(files.value);
      const hasFiles = isArray ? size(files.value) > 0 : !!files.value;
      const hasSingleFileFromArray = isArray && size(files.value) === 1;
      const firstFile = hasFiles
        ? hasSingleFileFromArray
          ? safeArray(files.value).at(0)
          : files.value
        : null;

      return {
        files: files.value,
        firstFile,
        hasFiles,
        isOverDropzone: isOverDropzone.value,
        totalSize: fileSizeTotal.value,
      };
    });

    let counter = 0;
    let isValid = true;

    /**
     * @param {DragEvent} e
     */
    function getFiles(e) {
      const list = Array.from(e?.dataTransfer?.files || []);
      return size(list) === 0 ? null : props.multiple ? list : list[0];
    }

    /**
     * @param {Array<string>} types
     */
    function checkDataTypes(types) {
      const dataTypes = allowedTypes.value;

      if (!dataTypes?.length) return true;

      if (types.length === 0) return false;

      return types.every((type) =>
        dataTypes.some((allowedType) => type.includes(allowedType))
      );
    }

    /**
     * @param {DataTransferItemList} items
     */
    function checkValidity(items = []) {
      const types = Array.from(items).map((item) => item.type);

      const dataTypesValid = checkDataTypes(types);
      const multipleFilesValid = props.multiple || items.length <= 1;

      return dataTypesValid && multipleFilesValid;
    }

    /**
     * @param {DragEvent} e
     * @param {'enter' | 'over' | 'leave' | 'drop'} type
     */
    function handleDragEvent(e, type) {
      if (!!props.disabled) return;

      const transferList = e.dataTransfer?.items;
      isValid = (!!transferList && checkValidity(transferList)) || false;

      const currentFiles = getFiles(e);

      e.preventDefault();

      switch (type) {
        case "enter":
          counter += 1;
          isOverDropzone.value = true;
          emit("enter", null, e);
          break;

        case "over":
          emit("over", null, e);
          break;

        case "leave":
          counter -= 1;
          if (counter === 0) isOverDropzone.value = false;
          emit("leave", null, e);

          break;

        case "drop":
          counter = 0;
          isOverDropzone.value = false;

          if (!isValid) return;

          files.value = currentFiles;
          emit("drop", currentFiles, e);

          break;
      }
    }

    onChange((e) => {
      if (!e) return;
      const listFiles = Array.from(e);
      if (!size(listFiles)) return;

      files.value = listFiles;
    });

    onReset(() => {
      files.value = null;
    });

    useEventListener(dropRef, "dragenter", (e) => handleDragEvent(e, "enter"));
    useEventListener(dropRef, "dragover", (e) => handleDragEvent(e, "over"));
    useEventListener(dropRef, "dragleave", (e) => handleDragEvent(e, "leave"));
    useEventListener(dropRef, "drop", (e) => handleDragEvent(e, "drop"));

    return {
      files: readonly(files),
      dropRef,
      result,
      open,
      reset,
    };
  },
});
</script>
